import { useState } from "react";
import { TypeAddInput } from "./TypeAddInput";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Building2, Users, Check } from "lucide-react";
import { ManagedCampaignData } from "./ManagedCampaignData";

interface ManagedIndustryStepProps {
  data: ManagedCampaignData;
  updateData: (updates: Partial<ManagedCampaignData>) => void;
}

const POPULAR_INDUSTRIES = [
  "Information Technology",
  "Financial Services",
  "Healthcare",
  "Manufacturing",
  "Retail & E-commerce",
  "Real Estate",
  "Education",
  "Consulting",
  "Marketing & Advertising",
  "Telecommunications",
];

const COMPANY_SIZES = [
  { label: "Startup (1-10)", value: "1-10" },
  { label: "Small (11-50)", value: "11-50" },
  { label: "Medium (51-200)", value: "51-200" },
  { label: "Large (201-1000)", value: "201-1000" },
  { label: "Enterprise (1000+)", value: "1000+" },
];

export const ManagedIndustryStep = ({ data, updateData }: ManagedIndustryStepProps) => {
  // ... existing state and handlers ...
  const [industryInput, setIndustryInput] = useState("");

  const toggleIndustry = (industry: string) => {
    if (data.industries.includes(industry)) {
      updateData({ industries: data.industries.filter(i => i !== industry) });
    } else {
      updateData({ industries: [...data.industries, industry] });
    }
  };

  const addIndustry = () => {
    const industry = industryInput.trim();
    if (industry && !data.industries.includes(industry)) {
      updateData({ industries: [...data.industries, industry] });
      setIndustryInput("");
    }
  };

  const handleIndustryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addIndustry();
    }
  };

  const toggleCompanySize = (size: string) => {
    if (data.companySizes.includes(size)) {
      updateData({ companySizes: data.companySizes.filter(s => s !== size) });
    } else {
      updateData({ companySizes: [...data.companySizes, size] });
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Industries */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm font-medium">
                Industries <span className="text-destructive">*</span>
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Select the industries your ideal customers work in
            </p>

            {/* Selected Industries Chips */}
            {data.industries.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {data.industries.map(industry => (
                  <Badge
                    key={industry}
                    variant="default"
                    className="gap-1 pl-2.5 pr-2 py-1.5 bg-success text-success-foreground hover:bg-success/90 border-transparent shadow-sm"
                  >
                    <Check className="w-3 h-3" />{industry}
                    <X
                      className="w-3 h-3 cursor-pointer hover:opacity-70"
                      onClick={() => toggleIndustry(industry)}
                    />
                  </Badge>
                ))}
              </div>
            )}

            <TypeAddInput placeholder="Type any industry, e.g. Healthcare" value={industryInput} onChange={setIndustryInput} onKeyDown={handleIndustryKeyDown} onAdd={() => addIndustry()} />

            {/* Popular Suggestions */}
            <div className="pt-2">
              <p className="text-xs text-muted-foreground mb-2">Popular suggestions:</p>
              <div className="flex flex-wrap gap-2">
                {POPULAR_INDUSTRIES.filter(i => !data.industries.includes(i)).map(industry => (
                  <Badge
                    key={industry}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-colors px-3 py-1.5 font-normal"
                    onClick={() => toggleIndustry(industry)}
                  >
                    + {industry}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Size */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm font-medium">
                Company Size <span className="text-destructive">*</span>
              </Label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {COMPANY_SIZES.map(size => {
                const isSelected = data.companySizes.includes(size.value);
                return (
                  <Button
                    key={size.value}
                    variant="outline"
                    className={`justify-between h-auto py-1.5 px-3 rounded-full text-xs ${isSelected ? "bg-success text-success-foreground hover:bg-success/90 border-transparent shadow-sm" : "hover:border-primary/50"}`}
                    onClick={() => toggleCompanySize(size.value)}
                  >
                    <span className="font-normal">{size.label}</span>
                    {isSelected && <Check className="h-4 w-4 ml-2" />}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
